import React, { useState } from 'react';
import { Card, Row, Col, Input, Button, Tag, Typography, Spin, Alert } from 'antd';
import {
    BarChartOutlined,
    SearchOutlined,
    RiseOutlined,
    ShopOutlined,
    ApartmentOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useGetAdminBrandsForAnalyticsQuery } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';

const { Title, Text } = Typography;
const { Search } = Input;

export const AdminAnalyticsPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');

    const { data: brands, isLoading, error } = useGetAdminBrandsForAnalyticsQuery({
        jwtToken: user?.jwtToken || '',
    });

    const handleBrandAnalyticsClick = (brandHash: string, brandName: string) => {
        navigate(`/admin/trends/brands/${brandHash}?brandName=${encodeURIComponent(brandName)}`);
    };

    const handleSearch = (value: string) => {
        setSearch(value.toLowerCase());
    };

    const filteredBrands = (Array.isArray(brands) ? brands : []).filter(brand => 
        search === '' || 
        brand.name.toLowerCase().includes(search) ||
        brand.industry.toLowerCase().includes(search)
    );

    const getIndustryColor = (industry: string) => {
        const colors = {
            'Technology': '#2563eb',
            'Consumer Goods': '#10b981', 
            'Fashion': '#8b5cf6',
            'Healthcare': '#f59e0b',
            'Finance': '#ef4444',
            'Automotive': '#06b6d4'
        };
        return colors[industry as keyof typeof colors] || '#64748b';
    };

    if (isLoading) {
        return (
            <div className="brand-analytics-container">
                <div className="brand-analytics-loading">
                    <Spin size="large" />
                    <div>Loading brands for analytics...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="brand-analytics-container">
                <Alert
                    message="Error Loading Brands"
                    description="Unable to load brand list for analytics. Please try again later."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ 
            padding: '24px',
            backgroundColor: '#f8fafc',
            minHeight: '100vh'
        }}>
            {/* Header */}
            <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '32px',
                marginBottom: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: '1px solid #f0f0f0'
            }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                }}>
                    <div>
                        <Title 
                            level={2} 
                            style={{ 
                                margin: 0, 
                                marginBottom: '8px',
                                fontSize: '28px',
                                fontWeight: 600,
                                color: '#1f2937',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}
                        >
                            <BarChartOutlined />
                            Brand Trends Dashboard
                        </Title>
                        <Text 
                            type="secondary" 
                            style={{ 
                                fontSize: '16px',
                                color: '#6b7280'
                            }}
                        >
                            Select a brand to view comprehensive analytics trends and insights
                        </Text>
                    </div>
                    
                    <div style={{
                        textAlign: 'right',
                        backgroundColor: '#f8fafc',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <Text 
                            style={{ 
                                fontSize: '24px',
                                fontWeight: 600,
                                color: '#2563eb',
                                display: 'block'
                            }}
                        >
                            {filteredBrands.length}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            brand{filteredBrands.length !== 1 ? 's' : ''} available
                        </Text>
                    </div>
                </div>
            </div>

            {/* Search Controls */}
            <div className="brand-analytics-controls">
                <div className="control-group">
                    <label className="control-label">
                        <SearchOutlined /> Search Brands
                    </label>
                    <Search
                        placeholder="Search by brand name or industry..."
                        onSearch={handleSearch}
                        onChange={(e) => setSearch(e.target.value.toLowerCase())}
                        className="brand-analytics-select"
                        style={{ minWidth: 300 }}
                    />
                </div>
            </div>

            {/* Brand Grid */}
            {filteredBrands.length === 0 ? (
                <div className="brand-analytics-empty">
                    <Title level={4}>No Brands Found</Title>
                    <p>No brands match your search criteria. Try adjusting your search terms.</p>
                </div>
            ) : (
                <Row gutter={[24, 24]}>
                    {filteredBrands.map((brand) => (
                        <Col key={brand.hash} xs={24} sm={12} md={8} lg={6}>
                            <Card
                                className="admin-brand-card brand-analytics-card"
                                onClick={() => handleBrandAnalyticsClick(brand.hash, brand.name)}
                                hoverable
                                style={{
                                    height: '420px',
                                    cursor: 'pointer',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                                    border: brand.isMock ? '2px solid #fa8c16' : '1px solid #f5f5f5',
                                    backgroundColor: brand.isMock ? '#fff7e6' : '#ffffff',
                                    transition: 'all 0.3s ease',
                                    overflow: 'visible'
                                }}
                                styles={{ body: {
                                        padding: '28px 24px 24px',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)'
                                    }
                                }}
                            >
                                <div>
                                    {/* Brand Icon */}
                                    <div style={{
                                        textAlign: 'center',
                                        marginBottom: '12px'
                                    }}>
                                        <div style={{
                                            width: '72px',
                                            height: '72px',
                                            borderRadius: '50%',
                                            backgroundColor: `${getIndustryColor(brand.industry)}12`,
                                            border: `2px solid ${getIndustryColor(brand.industry)}25`,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginBottom: '12px',
                                            boxShadow: `0 4px 12px ${getIndustryColor(brand.industry)}20`
                                        }}>
                                            <ShopOutlined 
                                                style={{ 
                                                    fontSize: '28px', 
                                                    color: getIndustryColor(brand.industry),
                                                    fontWeight: 600
                                                }} 
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Brand Name */}
                                    <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                                        <Title 
                                            level={4} 
                                            style={{ 
                                                margin: 0, 
                                                fontSize: '20px',
                                                fontWeight: 700,
                                                color: '#1a1a1a',
                                                lineHeight: 1.2,
                                                letterSpacing: '-0.02em'
                                            }}
                                        >
                                            {brand.name}
                                        </Title>
                                        {brand.isMock && (
                                            <Tag 
                                                color="orange" 
                                                style={{
                                                    fontSize: '10px',
                                                    marginTop: '4px'
                                                }}
                                            >
                                                MOCK DATA
                                            </Tag>
                                        )}
                                    </div>

                                    {/* Industry Tag */}
                                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                                        <Tag 
                                            style={{
                                                backgroundColor: `${getIndustryColor(brand.industry)}18`,
                                                color: getIndustryColor(brand.industry),
                                                border: `1px solid ${getIndustryColor(brand.industry)}40`,
                                                borderRadius: '20px',
                                                padding: '6px 16px',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            {brand.industry}
                                        </Tag>
                                    </div>

                                    {/* Organization ID */}
                                    <div style={{ 
                                        textAlign: 'center',
                                        backgroundColor: '#f8f9fa',
                                        padding: '6px 10px',
                                        borderRadius: '8px',
                                        border: '1px solid #e9ecef'
                                    }}>
                                        <Text 
                                            type="secondary" 
                                            style={{ 
                                                fontSize: '11px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                fontWeight: 500,
                                                color: '#6c757d'
                                            }}
                                        >
                                            <ApartmentOutlined style={{ fontSize: '11px' }} />
                                            ORG: {brand.organizationId}
                                        </Text>
                                    </div>
                                </div>

                                {/* Action Button */}
                                <div style={{ 
                                    borderTop: '1px solid #e9ecef', 
                                    paddingTop: '20px',
                                    textAlign: 'center'
                                }}>
                                    <Button 
                                        type="primary" 
                                        icon={<RiseOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBrandAnalyticsClick(brand.hash, brand.name);
                                        }}
                                        style={{
                                            borderRadius: '10px',
                                            fontWeight: 600,
                                            height: '40px',
                                            paddingLeft: '20px',
                                            paddingRight: '20px',
                                            fontSize: '14px',
                                            background: getIndustryColor(brand.industry),
                                            borderColor: getIndustryColor(brand.industry),
                                            boxShadow: `0 4px 12px ${getIndustryColor(brand.industry)}30`
                                        }}
                                    >
                                        View Analytics
                                    </Button>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Analytics Overview Card */}
            {filteredBrands.length > 0 && (
                <Card 
                    title="Analytics Features" 
                    style={{ marginTop: 24 }}
                    bordered={false}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={8}>
                            <Card type="inner" size="small">
                                <div style={{ textAlign: 'center' }}>
                                    <RiseOutlined style={{ fontSize: 24, color: 'var(--primary-color)' }} />
                                    <Title level={5}>Time Series Analysis</Title>
                                    <Text type="secondary">
                                        View brand equity trends over 3, 6, or 12 month periods with interactive charts
                                    </Text>
                                </div>
                            </Card>
                        </Col>
                        
                        <Col xs={24} md={8}>
                            <Card type="inner" size="small">
                                <div style={{ textAlign: 'center' }}>
                                    <BarChartOutlined style={{ fontSize: 24, color: 'var(--primary-color)' }} />
                                    <Title level={5}>Drill-Down Analysis</Title>
                                    <Text type="secondary">
                                        Click on metrics to drill down from latent elements to measured elements to individual statements
                                    </Text>
                                </div>
                            </Card>
                        </Col>
                        
                        <Col xs={24} md={8}>
                            <Card type="inner" size="small">
                                <div style={{ textAlign: 'center' }}>
                                    <SearchOutlined style={{ fontSize: 24, color: 'var(--primary-color)' }} />
                                    <Title level={5}>Multi-Metric Comparison</Title>
                                    <Text type="secondary">
                                        Compare up to 6 brand equity metrics simultaneously on the same chart
                                    </Text>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Card>
            )}
        </div>
    );
};

export default AdminAnalyticsPage;